import { zodResolver } from "@hookform/resolvers/zod";
import { DialogClose, DialogTrigger } from "@radix-ui/react-dialog";
import { CirclePlus } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useParams } from "react-router-dom";
import z from "zod";

import { OptionType } from "@/api/admin";
import {
  type AssignedRole,
  type AssignmentsUserRole,
  useNewAssignRoleMutation,
  UserLevel,
} from "@/api/admin/users";
import { QUERY_KEYS } from "@/api/constants";
import { queryClient } from "@/api/queryClient";
import {
  BaseSelect,
  CustomButton,
  CustomCardBody,
  DialogBody,
  type ItemsListType,
} from "@/components/atoms";
import {
  MultiSelectWithTags,
  SelectControl,
  toastSuccess,
  toastWarning,
} from "@/components/molecules";
import { Dialog } from "@/components/ui/dialog";
import { Form } from "@/components/ui/form";
import { commonIconProps20 } from "@/utils/constants";
import { usePartnerOptions } from "@/utils/hooks/usePartnerOptions";

import type { GroupedRole } from "../Tables/AdminAccountUsers/AccountUsersAssignedRoleTable";
import handleInviteError from "./handleInviteError";

interface AssignRoleProps {
  currentAssignments: AssignedRole[];
  groupedRoles: GroupedRole[];
}

const AssignRole = ({ currentAssignments, groupedRoles }: AssignRoleProps) => {
  const { id = "" } = useParams();
  const [open, setOpen] = useState(false);
  const { mutate, isPending } = useNewAssignRoleMutation();

  const { levelsList, optionsData, isOptionPending } = usePartnerOptions([
    OptionType.Groups,
    OptionType.Brands,
    OptionType.Branches,
  ]);

  const AssignRoleSchema = z
    .object({
      role: z.string().min(1, "Role is required"),
      level: z.string().min(1, "Level is required"),

      singleAssignment: z.string().optional(),
      multiAssignments: z.array(z.string()).optional(),
    })
    .superRefine((data, ctx) => {
      if (data.level === UserLevel.GROUP) {
        if (!data.singleAssignment) {
          ctx.addIssue({
            path: ["singleAssignment"],
            message: "Group is required",
            code: z.ZodIssueCode.custom,
          });
        }
      }

      if (data.level === UserLevel.BRAND || data.level === UserLevel.BRANCH) {
        if (!data.multiAssignments || data.multiAssignments.length === 0) {
          ctx.addIssue({
            path: ["multiAssignments"],
            message: "At least one item is required",
            code: z.ZodIssueCode.custom,
          });
        }
      }
    });

  type FormTypeAssignment = z.infer<typeof AssignRoleSchema>;

  const form = useForm<FormTypeAssignment>({
    resolver: zodResolver(AssignRoleSchema),
    defaultValues: {
      role: "",
      level: "",
      singleAssignment: "",
      multiAssignments: [],
    },
    mode: "onChange",
    reValidateMode: "onBlur",
  });

  const entityNameMap = useMemo(() => {
    if (!optionsData) return new Map();

    const all = [
      ...(optionsData?.groups ?? []),
      ...(optionsData?.brands ?? []),
      ...(optionsData?.branches ?? []),
    ];

    return new Map(all.map(item => [item.id, item.name]));
  }, [optionsData]);

  const onSubmit = async (data: FormTypeAssignment) => {
    const entityIds =
      data.level === UserLevel.GROUP
        ? data.singleAssignment
          ? [data.singleAssignment]
          : []
        : (data.multiAssignments ?? []);

    // const selectedRoleObject = filteredRolesList.find(r => r.id === data.role);

    const payload = {
      level: data.level,
      // TODO: delete after backend is ready
      role: "ADMIN",
      // role: selectedRoleObject?.name,
      roleId: data.role,
      entityIds,
    };

    mutate(
      {
        id: id,
        data: payload as AssignmentsUserRole,
      },
      {
        onSuccess: res => {
          const failedIds = res.uniqueFailedIds ?? [];
          const successIds = entityIds.filter(id => !failedIds.includes(id));

          failedIds.forEach(failedId => {
            const entityName = entityNameMap.get(failedId) || "Selected item";

            const sameEntityEntry = currentAssignments.find(
              item => item.entityId === failedId
            );

            const differentLevelEntry = currentAssignments.find(
              item => item.level !== data.level
            );

            if (sameEntityEntry && sameEntityEntry.role === data.role) {
              toastWarning(`${entityName} — this role is already assigned.`);
            } else if (differentLevelEntry) {
              toastWarning(
                `${entityName} — user already has a different level assigned (${differentLevelEntry.level}).`
              );
            } else if (
              sameEntityEntry &&
              sameEntityEntry.level === data.level
            ) {
              toastWarning(
                `${entityName} — this entity is already assigned with a different role.`
              );
            } else {
              if (data.level === UserLevel.GROUP) {
                toastWarning("A user can only be assigned to one group.");
              } else
                toastWarning(
                  `${entityName} — belongs to another ${data.level === UserLevel.BRAND ? "group" : "brand or group"} and cannot be assigned.`
                );
            }
          });

          const successNames = successIds
            .map(id => entityNameMap.get(id))
            .filter(Boolean);

          if (successNames.length) {
            toastSuccess(`${successNames.join(", ")} — assigned successfully.`);
          }

          if (successIds.length) {
            queryClient.invalidateQueries({
              queryKey: [QUERY_KEYS.adminUserRole, id],
            });
          }
        },
        onError: error => {
          handleInviteError(error, data.level as UserLevel, () =>
            onSubmit(data)
          );
        },
      }
    );
  };

  const {
    handleSubmit,
    formState: { isValid },
    control,
    setValue,
  } = form;

  const selectedLevel = useWatch({
    control,
    name: "level",
  }) as UserLevel | undefined;

  const nameConfigMap: Record<
    UserLevel,
    { label: string; items: ItemsListType }
  > = {
    [UserLevel.GROUP]: {
      label: "Group name",
      items: optionsData?.groups || [],
    },
    [UserLevel.BRAND]: {
      label: "Brand name",
      items: optionsData?.brands || [],
    },
    [UserLevel.BRANCH]: {
      label: "Branch name",
      items: optionsData?.branches || [],
    },
  };

  const isSelectedGroupLevel =
    selectedLevel === UserLevel.BRAND || selectedLevel === UserLevel.BRANCH;

  useEffect(() => {
    form.setValue("singleAssignment", "", {
      shouldDirty: false,
      shouldValidate: false,
    });

    form.setValue("multiAssignments", [], {
      shouldDirty: false,
      shouldValidate: false,
    });
  }, [selectedLevel]);

  const filteredRolesList = useMemo(() => {
    if (!selectedLevel || !groupedRoles) return [];

    const group = groupedRoles.find(g => g.level === selectedLevel);
    if (!group) return [];

    return group.roles;
  }, [selectedLevel, groupedRoles]);

  useEffect(() => {
    setValue("role", "");
  }, [selectedLevel, setValue]);

  useEffect(() => {
    if (open) {
      form.reset({
        role: "",
        level: "",
        singleAssignment: "",
        multiAssignments: [],
      });
    }
  }, [open]);

  if (isOptionPending) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <CustomButton
          type="button"
          className="ml-2 gap-[6px] px-[16px]! h-[32px] text-sm"
          size="xs"
          data-testid="assigned-role-assign-button"
        >
          <CirclePlus {...commonIconProps20} />
          <span>Assign role</span>
        </CustomButton>
      </DialogTrigger>
      <DialogBody title="Assign role" classes="overflow-visible!">
        <div className="flex flex-col gap-5 pt-2">
          <Form {...form}>
            <form
              onSubmit={handleSubmit(onSubmit)}
              data-testid="assign-role-form"
            >
              <CustomCardBody classes="pb-5 px-0">
                <div className="flex flex-col align-center gap-4.5">
                  <SelectControl<FormTypeAssignment>
                    form={form}
                    nameSelect="level"
                    label="Level"
                    placeholder="Select"
                    itemsList={levelsList}
                    triggerProps={{ "data-testid": "assign-role-select-level" }}
                  />

                  {!selectedLevel && (
                    <div className="flex flex-col gap-1">
                      <p className="text-sm">Name</p>
                      <BaseSelect
                        itemsList={[]}
                        disabled
                        placeholder="Select level first"
                      />
                    </div>
                  )}

                  {!isSelectedGroupLevel && selectedLevel && (
                    <SelectControl<FormTypeAssignment>
                      form={form}
                      nameSelect="singleAssignment"
                      label={
                        selectedLevel
                          ? nameConfigMap[selectedLevel].label
                          : "Name"
                      }
                      placeholder={
                        selectedLevel ? "Select" : "Select level first"
                      }
                      itemsList={
                        selectedLevel === UserLevel.GROUP
                          ? nameConfigMap.GROUP.items
                          : []
                      }
                      triggerProps={{
                        "data-testid": "invite-user-select-assignments-trigger",
                      }}
                      disabled={!selectedLevel}
                    />
                  )}

                  {isSelectedGroupLevel && selectedLevel && (
                    <div className="flex flex-col gap-1">
                      <p className="text-sm">
                        {nameConfigMap[selectedLevel].label}
                      </p>
                      <MultiSelectWithTags
                        form={form}
                        nameSelect="multiAssignments"
                        tagsList={nameConfigMap[selectedLevel].items}
                      />
                    </div>
                  )}
                  <SelectControl<FormTypeAssignment>
                    form={form}
                    nameSelect="role"
                    label="Role"
                    placeholder={
                      selectedLevel ? "Select role" : "Select level first"
                    }
                    itemsList={filteredRolesList}
                    disabled={!selectedLevel}
                    triggerProps={{ "data-testid": "assign-role-select-role" }}
                  />
                </div>
              </CustomCardBody>
              <div className="flex justify-between gap-3">
                <DialogClose asChild>
                  <CustomButton
                    variant="light"
                    type="button"
                    className="flex-1"
                    data-testid="assign-role-cancel-button"
                  >
                    Cancel
                  </CustomButton>
                </DialogClose>

                <DialogClose asChild>
                  <CustomButton
                    type="submit"
                    className="flex-1"
                    data-testid="assign-role-submit-button"
                    disabled={!isValid}
                    isPending={isPending}
                  >
                    Send invite
                  </CustomButton>
                </DialogClose>
              </div>
            </form>
          </Form>
        </div>
      </DialogBody>
    </Dialog>
  );
};

export default AssignRole;
